// PhotoID State: 
import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image
} from "react-native";
import { useTranslation } from 'react-i18next';
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import SearchIcon from "../../../../../../../../assets/images/search_icon.png";

type StateItem = { stateid: number; stateAbbrivation: string; stateName: string };

type ChooseStateOfIdStepProps = {
  onCountrySelected?: () => void;
};
export default function ChooseStateOfIdStep({ onCountrySelected }: ChooseStateOfIdStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [states, setStates] = useState<StateItem[]>([]);
  const setSelectedIdPhotoIdStateAbbr = useSessionStore((s: SessionState) => s.setSelectedIdPhotoIdStateAbbr);
  const setSelectedIdPhotoIdStateName = useSessionStore((s: SessionState) => s.setSelectedIdPhotoIdStateName);
  const selectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdCountry);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await loadCountries();
        const list = (result?.data ?? []) as any[];
        const targetCountryCode = selectedIdPhotoIdCountry || 'USA';
        const country = list.find((c) => c.CountryCode === targetCountryCode);
        const countryStates: StateItem[] = Array.isArray(country?.States) ? country.States : [];
        setStates(countryStates);
      } catch (e) {
        console.error('Failed to load states', e);
      }
    };
    fetchStates();
  }, [selectedIdPhotoIdCountry]);

  // Filter states by search
  const filteredState = useMemo(() => {
    const q = search.toLowerCase();
    return states.filter((s) => (s.stateName || '').toLowerCase().includes(q));
  }, [states, search]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("CHOOSE_STATE_OF_ID")}</Text>
      <Text style={styles.subtitle}>
        {t("SELECT_STATE_OF_ISSUE")}
      </Text>

      {/* Search bar */}
      <View style={styles.searchContent}>
        <Image
          source={SearchIcon}
          style={{ height: 20, width: 20, marginRight: 10}}
        />
        <TextInput
          style={styles.searchInput}
          placeholder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      {/* Country list */}
      <FlatList
        data={filteredState}
        keyExtractor={(item) => item.stateAbbrivation}
        style={{ flex: 1, width: "100%" }} 
        renderItem={({ item }) => (
          <TouchableOpacity
            style={styles.countryRow}
            onPress={() => {
              setSelectedState(item.stateName);
              setSelectedIdPhotoIdStateName?.(item.stateName);
              setSelectedIdPhotoIdStateAbbr?.(item.stateAbbrivation);
              onCountrySelected?.();
            }}
          >
            <Text style={styles.countryName}>{item.stateName}</Text>
            <View
              style={[
                styles.radioOuter,
                selectedState === item.stateName && styles.radioOuterSelected,
              ]}
            >
              {selectedState === item.stateName && <View style={styles.radioInner} />}
            </View>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    marginTop: 16,
    backgroundColor: '#FFF'
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 16,
  },
  searchContent: {
    flexDirection: 'row',
    width: '100%',
    backgroundColor: "#F3F4F6",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    alignItems: 'center'
  },
  searchInput: {
    fontSize: 16,
  },
  countryRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    width: "100%",          // <-- stretch full width
    backgroundColor: "#fff",
    paddingHorizontal: 16,
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    flex: 1,
    fontSize: 16,
    color: "#111827",
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#000",
  },
  selectedText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: "500",
    color: "#111827",
  },
});
